import {
  ApproveTeamRequest,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  TeamRoleChips,
} from '@dataroom/shared';
import type { AdminBuyerTeamRequest } from '@dataroom/shared';

/**
 * Buyers' waiting team asks (migration 0075), room-wide, on the Buyers page: a
 * buyer asked to add a person to their team; the intermediary approves (the
 * person is added and invited) or declines. An ask that can't be approved — the
 * seller's side, tenant staff, or another bidder — says why, and only Decline is
 * offered. Hidden when nothing is waiting. Presentational.
 */
export function TeamRequestsCard({
  requests,
  decidingId,
  error,
  onApprove,
  onDecline,
}: {
  requests: readonly AdminBuyerTeamRequest[];
  decidingId: string | null;
  error: string | null;
  onApprove: (requestId: string, expiresAt: string) => void;
  onDecline: (requestId: string) => void;
}) {
  if (requests.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Team requests <HelpTip id="admin.buyers.team-requests" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Buyers asked to add these people to their team. Approved people are invited, sign the NDA,
          and then see what that buyer sees. You choose how long each person's access lasts as you
          approve.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <ul className="divide-y rounded-md border" aria-label="Waiting team requests">
          {requests.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="min-w-0 space-y-0.5">
                <p className="break-all text-sm font-medium">
                  {r.email} <TeamRoleChips roles={r.roles} className="align-middle" />
                </p>
                <p className="break-all text-xs text-muted-foreground">
                  For the team of {r.leadEmail}
                </p>
                {r.problem ? (
                  <p className="text-xs text-destructive">{r.problem}</p>
                ) : r.existingAccount ? (
                  <p className="text-xs text-muted-foreground">
                    Existing account — approving re-invites them.
                  </p>
                ) : null}
              </div>
              <ApproveTeamRequest
                disabled={decidingId === r.id}
                cannotApprove={r.problem !== null}
                onApprove={(expiresAt) => onApprove(r.id, expiresAt)}
                onDecline={() => onDecline(r.id)}
              />
            </li>
          ))}
        </ul>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
